import { devlog } from "../../util/utils.ts";
import type { VPData } from '../../data/data-interfaces.ts';
import styleText from './as-viewport.css?raw';
import '../base/as-spinner.ts';
import { DropOverlay, DragDropHandler, sendDroppedFiles } from '../../features/importExport/import/fileBase.ts';
import { sendEv } from '../../data/events.ts';

// --- Camera types ---

interface CameraParams {
  right: number[];
  top: number[];
  view: number[];
  focus: number[];
  origin: number[];
  aperture: number;
}

// --- Constants ---

const PERSPECTIVE_APERTURE = 35.0;
const ORTHO_APERTURE = 0.001;
const DISTANCE_FACTOR = 25000;

const MIRRORED_VIEWS: Record<string, string> = {
  top: 'bottom', bottom: 'top',
  left: 'right', right: 'left',
  front: 'back', back: 'front',
};

// --- Ported math utilities (from Paramate.Math3D) ---

function scalarProd3(a: number[], b: number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function dist3(a: number[], b: number[]): number {
  const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
// --- View generation (adapted from working toggleDefaultViews) ---

function buildDefaultViews(camParams: CameraParams, apertureVal: number): Record<string, CameraParams> {
  const o = camParams.origin;
  const d = dist3(o, camParams.focus);
  const s3 = 1 / Math.sqrt(3);

  return {
    isometric: {
      aperture: apertureVal,
      focus:  [o[0] + d * s3, o[1] + d * s3, o[2] + d * s3],
      origin: o,
      view:   [-s3, -s3, -s3],
      right:  [-0.70710678, 0.70710678, 0],
      top:    [-0.40824829, -0.40824829, 0.81649658],
    },
    front: {
      aperture: apertureVal,
      focus:  [o[0], o[1] - d, o[2]],
      origin: o,
      right:  [1, 0, 0],
      top:    [0, 0, 1],
      view:   [0, 1, 0],
    },
    back: {
      aperture: apertureVal,
      focus:  [o[0], o[1] + d, o[2]],
      origin: o,
      right:  [-1, 0, 0],
      top:    [0, 0, 1],
      view:   [0, -1, 0],
    },
    top: {
      aperture: apertureVal,
      focus:  [o[0], o[1], o[2] + d],
      origin: o,
      right:  [1, 0, 0],
      top:    [0, 1, 0],
      view:   [0, 0, -1],
    },
    bottom: {
      aperture: apertureVal,
      focus:  [o[0], o[1], o[2] - d],
      origin: o,
      right:  [1, 0, 0],
      top:    [0, -1, 0],
      view:   [0, 0, 1],
    },
    left: {
      aperture: apertureVal,
      focus:  [-(o[0] - d), o[1], o[2]],
      origin: o,
      right:  [0, 1, 0],
      top:    [0, 0, 1],
      view:   [-1, 0, 0],
    },
    right: {
      aperture: apertureVal,
      focus:  [o[0] - d, o[1], o[2]],
      origin: o,
      right:  [0, -1, 0],
      top:    [0, 0, 1],
      view:   [1, 0, 0],
    },
  };
}

// --- Component ---

export class AsViewport extends HTMLElement {
  static tag = "as-viewport"
  public paramateVp: any;
  private _resizeObserver: ResizeObserver | null = null;
  private _pendingRestore: { sessionId: string; data: VPData } | null = null;
  public perspectiveView = false;
  private _apertureVal = ORTHO_APERTURE;

  private _skipCamCtrlEvs = false;
  private _handleConfiguratorInitialized = (event: CustomEvent) => {
    if (!event.detail?.configurator?.viewPort) return;
    this.paramateVp = event.detail.configurator.viewPort;

    this.paramateVp.updateViewport();

    // Handle window resize
    window.addEventListener('resize', () => {
      this.paramateVp.updateViewport();
    });

    // Handle container resize (e.g., when inspector opens/closes)
    this._resizeObserver = new ResizeObserver(() => {
      this.paramateVp.updateViewport();
    });
    this._resizeObserver.observe(this);

    setTimeout(() => {
      this._dispatchCameraUpdate();
    }, 100);

    const userOnAnimCallback = this.paramateVp.onModelAnimationFrame;
    this.paramateVp.onModelAnimationFrame = () => {
      userOnAnimCallback?.();
      this._dispatchCameraUpdate();
    }
    sendEv(this, 'as-viewport-ready', {
      detail: { viewport: this.paramateVp }
    });

    sendEv(window, 'as-viewport-class', {
      detail: {
        vpComponent: this,
        viewport: this.paramateVp
      }
    });

    // Detect initial perspective state from camera and broadcast
    const initCam = this.paramateVp.getCameraParams?.();
    if (initCam) {
      this.perspectiveView = initCam.aperture > 1;
      this._apertureVal = this.perspectiveView ? PERSPECTIVE_APERTURE : ORTHO_APERTURE;
    }
    sendEv(window, 'as-vp-persp-update', { detail: { perspective: this.perspectiveView } });

    // Process any mesh restore that was queued before viewport was ready
    this._processPendingRestore();
  };
  private _camTransitionSpeed = 0;
  private _dropOverlay!: DropOverlay;
  private _dragDropHandler: DragDropHandler | null = null;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes(): string[] {
    return ['skip-cam-ctrl-evs', 'cam-transition-speed'];
  }

  get skipCamCtrlEvs(): boolean { return this._skipCamCtrlEvs; }
  set skipCamCtrlEvs(val: boolean) {
    this._skipCamCtrlEvs = val;
    if (val) this.setAttribute('skip-cam-ctrl-evs', '');
    else this.removeAttribute('skip-cam-ctrl-evs');
  }

  get camTransitionSpeed(): number { return this._camTransitionSpeed; }
  set camTransitionSpeed(val: number) {
    this._camTransitionSpeed = val;
    this.setAttribute('cam-transition-speed', String(val));
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: 'open' });
    shadow.adoptedStyleSheets = [
      ...shadow.adoptedStyleSheets,
      AsViewport._sheet
    ];
    shadow.innerHTML = `<slot></slot>`;

    this._dropOverlay = new DropOverlay(shadow);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'skip-cam-ctrl-evs':
        this._skipCamCtrlEvs = newValue !== null;
        break;
      case 'cam-transition-speed':
        this._camTransitionSpeed = Number(newValue) || 0;
        break;
    }
  }

  /**
   * Moves camera to target view using moveCamera with autoPos handling.
   */
  private _moveCameraTo(target: CameraParams): void {
    const wasAuto = this.paramateVp.getCameraAutoPos();
    if (wasAuto) this.paramateVp.setCameraAutoPos(false);
    this.paramateVp.moveCamera(target, () => {
      if (wasAuto) this.paramateVp.setCameraAutoPos(true);
      this.paramateVp.autoPosCamera(true);
    }, this._camTransitionSpeed, 30);
  }

  // --- Camera control handler ---

  private _handleButtonClick = (e: Event): void => {
    if (!this.paramateVp || this._skipCamCtrlEvs) return;
    const action = (e as CustomEvent).detail?.buttonName;
    if (!action) return;

    switch (action) {
      case 'toggle-perspective': {
        this.perspectiveView = !this.perspectiveView;
        this._apertureVal = this.perspectiveView ? PERSPECTIVE_APERTURE : ORTHO_APERTURE;
        const cam = this.paramateVp.getCameraParams();
        for (let i = 0; i < cam.focus.length; i++) {
          const f = cam.focus[i] - cam.origin[i];
          cam.focus[i] = !this.perspectiveView
            ? cam.origin[i] + f * DISTANCE_FACTOR
            : cam.origin[i] + f / DISTANCE_FACTOR;
        }
        cam.aperture = this._apertureVal;
        this.paramateVp.setCamera(cam, true);
        sendEv(window, 'as-vp-persp-update', { detail: { perspective: this.perspectiveView } });
        break;
      }

      case 'view-fit':
        this.paramateVp.autoPosCamera(true);
        break;

      case 'view-iso': {
        const views = buildDefaultViews(this.paramateVp.getCameraParams(), this._apertureVal);
        this._moveCameraTo(views['isometric']);
        break;
      }

      case 'view-front':
      case 'view-right':
      case 'view-top': {
        const viewName = action.replace('view-', '');
        const cam = this.paramateVp.getCameraParams();
        const views = buildDefaultViews(cam, this._apertureVal);
        if (!views[viewName]) break;
        const val = scalarProd3(cam.view, views[viewName].view);
        const selectedView = (val > -1 && val < 0) || val === 1
          ? (MIRRORED_VIEWS[viewName] || viewName)
          : viewName;
        this._moveCameraTo(views[selectedView]);
        break;
      }
    }
  };

  // Drag-and-drop is handled by DragDropHandler (see connectedCallback).

  connectedCallback() {
    window.addEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized);

    // Listen for as-get-paramate-vp events and respond with viewport instance if available
    window.addEventListener('as-get-paramate-vp', this._handleGetParamateVp);

    // Camera control events from header view buttons
    window.addEventListener('as-button-click', this._handleButtonClick);

    // Capture phase: fires before children; stopPropagation in the callback
    // prevents any parent drop zone (e.g. as-app file-droppable) from also handling the same drop.
    this._dragDropHandler = new DragDropHandler(
      this,
      (files, e) => {
        e.stopPropagation();
        sendDroppedFiles(this, 'viewport', files);
      },
      this._dropOverlay,
      true,
    );
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized);
    window.removeEventListener('as-get-paramate-vp', this._handleGetParamateVp);
    window.removeEventListener('as-button-click', this._handleButtonClick);
    this._dragDropHandler?.destroy();
    this._dragDropHandler = null;
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  /**
   * Handle as-get-paramate-vp event
   */
  private _handleGetParamateVp = (event: CustomEvent): void => {
   devlog("AsViewport: Handling as-get-paramate-vp event, have viewport:", !!this.paramateVp);
    if (this.paramateVp) {
      event.detail.resolve(this.paramateVp);
    }
  }

  /**
   * Handle configurator created event
   */
  /**
   * Process any pending mesh restoration after viewport is ready
   */
  private _processPendingRestore(): void {
    if (!this._pendingRestore) return;

    const { sessionId, data } = this._pendingRestore;
    this._pendingRestore = null;

    const success = this.restoreMesh(data, sessionId);
    if (success) {
      devlog(`as-viewport: Restored pending mesh for sessionId "${sessionId}"`);
    }
  }

  /**
   * Extracts the current mesh data from the viewport's viewer.
   * Returns the mesh geometry as flattened Float32Arrays.
   * @param modelId - Optional model ID to extract. If not provided, extracts the currently bound model.
   * @returns VPData object or null if no mesh is available
   */
  extractMesh(modelId?: string): VPData | null {
    const viewer = this.paramateVp?.oViewer;
    if (!viewer) {
      console.warn('extractMesh: No viewer available');
      return null;
    }

    let model = viewer.model;

    // If a specific modelId is requested, find it in the models array
    if (modelId && viewer.models) {
      const found = viewer.models.find((m: any) => m.id === modelId);
      if (found) {
        model = found.model;
      } else {
        console.warn(`extractMesh: Model with id "${modelId}" not found`);
        return null;
      }
    }

    if (!model) {
      console.warn('extractMesh: No model currently bound');
      return null;
    }

    return {
      vertices: model.vertices,
      normals: model.normals,
      colors: model.colors,
      drawGroups: model.drawGroups || [],
      boundingBox: model.boundingBox || { min: [0, 0, 0], max: [0, 0, 0], center: [0, 0, 0] }
    };
  }

  /**
   * Restores mesh data to the viewport's viewer.
   * @param data - VPData object containing mesh geometry
   * @param modelId - Optional model ID to assign. Defaults to 'restored'
   * @returns true if successful, false otherwise
   */
  restoreMesh(data: VPData, modelId: string = 'restored'): boolean {
    const viewer = this.paramateVp?.oViewer;
    if (!viewer) {
      console.warn('restoreMesh: No viewer available');
      return false;
    }

    if (!data || !data.vertices || !data.normals || !data.colors) {
      console.warn('restoreMesh: Invalid mesh data provided');
      return false;
    }

    try {
      const model = {
        vertices: data.vertices,
        normals: data.normals,
        colors: data.colors,
        drawGroups: data.drawGroups || [],
        boundingBox: data.boundingBox || { min: [0, 0, 0], max: [0, 0, 0], center: [0, 0, 0] }
      };

      if (viewer.models) {
        const existingIndex = viewer.models.findIndex((m: any) => m.id === modelId);
        if (existingIndex !== -1) {
          viewer.models.splice(existingIndex, 1);
        }
        viewer.models.push({ id: modelId, model });
      }

      viewer.model = model;
      if (viewer.bindModel) {
        viewer.bindModel(model);
      }

      if (this.paramateVp.updateViewport) {
        this.paramateVp.updateViewport();
      }

      return true;
    } catch (error) {
      console.error('restoreMesh: Failed to restore mesh', error);
      return false;
    }
  }

  /**
   * Dispatches camera update event to all children
   */
  private _dispatchCameraUpdate(): void {
    const cam = this.paramateVp?.getCameraParams?.();
    if (cam) {
      const axisDisplay = this.querySelector('as-axis-display');
      if (axisDisplay) {
        sendEv(axisDisplay, 'as-viewport-camera-update', {
          bubbles: false,
          detail: { cam }
        });
      }

      sendEv(window, 'as-viewport-camera-update', { detail: { cam } });
    }
  }
}

// Check if the element is already defined before registering it
if (!customElements.get(AsViewport.tag)) {
  customElements.define(AsViewport.tag, AsViewport);
} else {
  console.warn("as-viewport already defined");
}
